import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { CallButton, ChatButton, AdvisorAvatar } from 'components';
import useObserver from 'hooks/useObserver';
import {
  EVENT_ACTION_TYPES,
  LISTINGS_CALL_FLOW,
  LISTINGS_CHAT_FLOW,
  PAGE_ID,
  SCREEN
} from 'libs/constants';
import Link from 'next/link';
import { StarRating, PriceTag } from './@components';
import {
  AdvisorDescription,
  AdvisorName,
  AdvisorPitch,
  AdvisorCardContainer,
  FirstLine,
  SecondLine,
  DataPairWrapper,
  CallButtonContainer,
  ChatButtonContainer
} from './styled';

function DataPairComp({
  firstLine,
  secondLine
}: {
  firstLine: number | string | JSX.Element;
  secondLine: string;
}) {
  return (
    <DataPairWrapper>
      <FirstLine className="first-line">{firstLine}</FirstLine>
      <SecondLine className="second-line">{secondLine}</SecondLine>
    </DataPairWrapper>
  );
}

const formatReviewCount = (reviewCount: number) => {
  let formatedReviewCount = reviewCount.toString();
  if (reviewCount > 9999)
    formatedReviewCount = `${Math.floor(reviewCount / 1000)}K`;
  else if (reviewCount > 999)
    formatedReviewCount = reviewCount.toLocaleString('en-US');
  return formatedReviewCount;
};

function AdvisorCard({
  advisorInfo: {
    speakerName,
    pictureURL,
    salespitch,
    listingTitle,
    averageRating,
    starRating,
    ratingCount,
    lifetimeTransactionCount,
    memberSince,
    serviceRate,
    optedIntoOffers,
    chatAvailability,
    availability,
    offerPrice,
    id,
    majorRank,
    categoryId,
    detailsUrl,
    picture: {
      thumbnailVersion: { url: thumbnailPictureURL }
    },
    advisorId
  },
  pageId,
  gid,
  pmode,
  webTrack,
  chatTrackingSource,
  flBidId,
  eligibleForFeaturedListingClicks,
  appointmentTargetUri,
  returnUrl,
  cancelUrl,
  flmode,
  onCallButtonClick,
  onChatButtonClick,
  captureAdvisorListingImpression,
  position
}: IAdvisorCard): JSX.Element {
  const unSubscribeObserverRef = useRef(false);
  const [ref, entry] = useObserver({
    threshold: 0.5,
    isUnsubscribe: unSubscribeObserverRef.current
  });

  if (entry && entry.isIntersecting) {
    if (captureAdvisorListingImpression && !unSubscribeObserverRef.current) {
      captureAdvisorListingImpression(
        EVENT_ACTION_TYPES.viewed,
        id,
        advisorId,
        position,
        availability,
        chatAvailability
      );
    }
    unSubscribeObserverRef.current = true;
  }

  const reviewValue = (
    <StarRating averageRating={averageRating} starRating={starRating} />
  );
  const reviewLabel = `${formatReviewCount(ratingCount)} review${
    ratingCount > 1 ? 's' : ''
  }`;
  const readingsLabel = `reading${lifetimeTransactionCount > 1 ? 's' : ''}`;
  let advisorCallWebTrack = 0;
  let advisorChatWebTrack = 0;
  let screen = '';

  if (pageId === PAGE_ID.homePage) {
    advisorCallWebTrack = LISTINGS_CALL_FLOW.WebHomePageADPLinkCallButton;
    advisorChatWebTrack = LISTINGS_CHAT_FLOW.WebHomePageADPLinkChatButton;
    screen = SCREEN.homePage;
  }

  const advisorDetailsUrl = `
    ${detailsUrl}
    &gid=${gid}
    &pmode=${pmode || 0}
    &flmode=${flmode}
    &pageid=${pageId}
    &callwebtrack=${advisorCallWebTrack}
    &chatwebtrack=${advisorChatWebTrack}
    &screen=${screen}
    `
    .replaceAll('\n', '')
    .replaceAll(' ', '');
  const defaultImageURL = 'https://si.keen.com/D1_default-advisor-img_V1.png';

  const redirectToDetailsPage = () => {
    if (captureAdvisorListingImpression) {
      captureAdvisorListingImpression(
        EVENT_ACTION_TYPES.clicked,
        id,
        advisorId,
        position,
        availability,
        chatAvailability
      );
    }
    window.location.href = advisorDetailsUrl;
  };

  return (
    <AdvisorCardContainer>
      <Grid container columns={16} spacing={1}>
        <Grid data-listingid={id} item xs={16} md={10} ref={ref}>
          <div data-testid="advisor-card">
            <Grid
              className="advisor-card-profile"
              onClick={redirectToDetailsPage}
              container
              columns={16}
              columnSpacing={5}>
              <Grid item xs={4} sm={3} md={4}>
                <AdvisorAvatar
                  src={pictureURL || defaultImageURL}
                  alt=""
                  placeholderImgURL={thumbnailPictureURL || defaultImageURL}
                  size="medium"
                  width={175}
                  height={175}
                />
              </Grid>
              <Grid className="advisor-data" item xs={12} sm={13} md={12}>
                <AdvisorName>
                  <Link href={advisorDetailsUrl}>{speakerName}</Link>
                </AdvisorName>
                <AdvisorPitch>{listingTitle}</AdvisorPitch>
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'inherit', lg: 'inherit' }
                  }}>
                  <AdvisorDescription>{salespitch}</AdvisorDescription>
                </Box>
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid
          className="advisor-card-details"
          item
          xs={16}
          md={3}
          onClick={redirectToDetailsPage}
          sx={{ cursor: 'pointer' }}>
          <Grid container columns={16} spacing={4} marginTop={0}>
            <Grid className="advisor-review" item xs={8} sm={8} md={16}>
              <DataPairComp firstLine={reviewValue} secondLine={reviewLabel} />
            </Grid>
            <Grid
              className="advisor-readings"
              item
              md={16}
              sx={{
                display: {
                  xs: 'none',
                  sm: 'none',
                  md: 'block',
                  lg: 'block',
                  xl: 'block'
                }
              }}>
              <DataPairComp
                firstLine={
                  lifetimeTransactionCount > 999
                    ? lifetimeTransactionCount.toLocaleString('en-US')
                    : lifetimeTransactionCount
                }
                secondLine={readingsLabel}
              />
            </Grid>
            <Grid
              className="advisor-since"
              item
              md={16}
              sx={{
                display: {
                  xs: 'none',
                  sm: 'none',
                  md: 'block',
                  lg: 'block',
                  xl: 'block'
                }
              }}>
              <DataPairComp
                firstLine={new Date(memberSince).getFullYear()}
                secondLine="Advisor since"
              />
            </Grid>
            <Grid
              className="offer-price"
              item
              xs={8}
              sm={8}
              sx={{
                display: {
                  xs: 'block',
                  sm: 'block',
                  md: 'none',
                  lg: 'none',
                  xl: 'none'
                }
              }}>
              <PriceTag
                optedIntoOffers={optedIntoOffers}
                serviceRate={serviceRate}
                offerPrice={offerPrice}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          className="advisor-card-price-call-chat-container"
          item
          xs={16}
          md={3}>
          <Grid
            sx={{
              display: {
                xs: 'none',
                sm: 'none',
                md: 'block',
                lg: 'block',
                xl: 'block'
              }
            }}>
            <PriceTag
              optedIntoOffers={optedIntoOffers}
              serviceRate={serviceRate}
              offerPrice={offerPrice}
            />
          </Grid>
          <Grid container columns={16} spacing={0}>
            <Grid item xs={8} sm={8} md={16}>
              <CallButtonContainer>
                <CallButton
                  callAvailability={availability}
                  pmode={pmode}
                  displayPrice
                  listingId={id}
                  returnUrl={returnUrl}
                  cancelUrl={cancelUrl}
                  webTrack={webTrack}
                  majorRank={majorRank}
                  flBidId={flBidId}
                  eligibleForFeaturedListingClicks={
                    eligibleForFeaturedListingClicks
                  }
                  parentCategoryId={categoryId}
                  appointmentTargetUri={appointmentTargetUri}
                  onCallButtonClick={onCallButtonClick}
                />
              </CallButtonContainer>
            </Grid>
            <Grid item xs={8} sm={8} md={16}>
              <ChatButtonContainer>
                <ChatButton
                  chatAvailability={chatAvailability}
                  listingId={id}
                  pmode={pmode}
                  chatTrackingSource={chatTrackingSource}
                  eligibleForFeaturedListingClicks={
                    eligibleForFeaturedListingClicks
                  }
                  flBidId={flBidId}
                  parentCategoryId={categoryId}
                  onChatButtonClick={onChatButtonClick}
                />
              </ChatButtonContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AdvisorCardContainer>
  );
}

export default AdvisorCard;
